/**
 * Supersubs — jQuery plugin for adjusting submenu widths
 *
 * Originally by Joel Birch (v0.3b, 2013, MIT/GPL).
 * Rewritten in TypeScript for the tagdiv theme refactor.
 *
 * Automatically adjusts submenu widths of suckerfish-style menus to
 * match their longest list item children, measured in em units.
 */
import $ from 'jquery';

interface SupersubsOptions {
    /** Minimum submenu width in em units */
    minWidth: number;
    /** Maximum submenu width in em units */
    maxWidth: number;
    /** Extra width (em) added to prevent line wrapping in some browsers */
    extraWidth: number;
}

const defaults: SupersubsOptions = {
    minWidth: 9,
    maxWidth: 25,
    extraWidth: 0,
};

$.fn.supersubs = function (options?: Partial<SupersubsOptions>): JQuery {
    const opts: SupersubsOptions = $.extend({}, defaults, options);

    return this.each(function () {
        const $menu = $(this);

        // Show all sub-menus so we can measure them
        const $subMenus = $menu.find('ul').show();

        // Measure 1em in pixels by inserting a temporary em-dash character.
        // .css('fontSize') returns inconsistent results across browsers, so
        // measuring an actual rendered character is more reliable.
        const $fontProbe = $('<li id="menu-fontsize">&#8212;</li>').css({
            padding: 0,
            position: 'absolute',
            top: '-999em',
            width: 'auto',
        }).appendTo($menu);

        const fontSizePx = $fontProbe[0].clientWidth;
        $fontProbe.remove();

        // Adjust each sub-menu's width
        $subMenus.each(function () {
            const $ul = $(this);
            const $lis = $ul.children();
            const $anchors = $lis.children('a');

            // Force single-line measurement: set nowrap and save current float
            const liFloat = $lis.css('white-space', 'nowrap').css('float');

            // Remove width/float constraints so the UL shrink-wraps to content
            $ul.add($lis).add($anchors).css({
                float: 'none',
                width: 'auto',
            });

            // The UL is now shrink-wrapped (position: absolute in CSS).
            // Convert its pixel width to em units.
            let emWidth = $ul[0].clientWidth / fontSizePx + opts.extraWidth;

            // Clamp to configured range
            emWidth = Math.max(opts.minWidth, Math.min(opts.maxWidth, emWidth));

            // Apply the width
            $ul.css('width', emWidth + 'em');

            // Restore list items: float, full width, normal wrapping
            $lis.css({
                float: liFloat,
                width: '100%',
                'white-space': 'normal',
            }).each(function () {
                // Reposition any nested sub-menus to sit alongside the parent
                const $childUl = $(this).children('ul');
                const offsetDir = $childUl.css('left') !== undefined ? 'left' : 'right';
                $childUl.css(offsetDir, '100%');
            });
        }).hide(); // Re-hide sub-menus after measurement
    });
};

// Expose defaults on the plugin function for external overrides
($.fn.supersubs as any).defaults = defaults;
